import React from "react";
import formatDate from "@ui/i18n/formatDate";
import useAxisMinMax, { type MinMax } from "@ui/Highcharts/hooks/useAxisMinMax";
import { useReportingChartPeriod } from "./useReportingChartPeriod";
import isFuncState from "../../../lib/isFuncState";
import type { Reporting, ReportingPeriod } from "../../../api/useReporting";

const AXIS_ID = "reporting-period";

export function formatPeriodAxis(context: Highcharts.AxisLabelsFormatterContextObject) {
  return formatDate(Number(context.value), "month-date", null);
}

export function useReportingPeriodAxis(
  chart: React.RefObject<Highcharts.Chart>,
  reporting: Reporting,
  reportingPeriod: ReportingPeriod | null,
) {
  const [reportingChartPeriod, setReportingChartPeriod] = useReportingChartPeriod(
    reporting,
    reportingPeriod,
  );

  const setMinMax = React.useCallback(
    (value: React.SetStateAction<MinMax>) => {
      // In the case of useAxisMinMax hook, we except a function to be passed as a parameter
      if (isFuncState(value)) {
        setReportingChartPeriod(oldPeriod => {
          const newPeriod = value({ min: oldPeriod.from, max: oldPeriod.to });
          return { from: Number(newPeriod.min), to: Number(newPeriod.max) };
        });
      } else {
        setReportingChartPeriod({ from: Number(value.min), to: Number(value.max) });
      }
    },
    [setReportingChartPeriod],
  );

  const { initialUserMin, initialUserMax } = useAxisMinMax({
    chartRef: chart,
    axisId: AXIS_ID,
    minMax: {
      min: reportingChartPeriod.from,
      max: reportingChartPeriod.to,
    },
    setMinMax,
  });

  return React.useMemo(
    () => ({
      id: AXIS_ID,
      type: "datetime" as Highcharts.AxisTypeValue,
      tickPixelInterval: 150,
      labels: {
        formatter: formatPeriodAxis,
      },
      initialUserMin,
      initialUserMax,
    }),
    [initialUserMax, initialUserMin],
  );
}
